import {deployedSite} from "../../deployedTopology.mjs";
import {brandHostFor} from "nav/siteLinkOrigin";

/**
 * The claimed-subdomain rules (kitecorp/transferify-api-service#350), React-free by mandate: the editor
 * and its hook only render what this module decides.
 *
 * Client-side functions return a message KEY, never copy — the catalog owns the words in both locales, and
 * a module that returned English would make the Romanian build silently wrong.
 */

/** DNS's limit for a single label, and the api's `@Size` bound on the claim request. */
export const MAX_LABEL_LENGTH = 63;

/**
 * One lowercase LDH label, mirroring `SubdomainClaims.LABEL_PATTERN` in the api.
 *
 * The api is the enforcement — it re-validates every claim through the one unbypassable issuance gate — so
 * this copy exists purely to spare the user a round trip on an obviously malformed name. It is deliberately
 * NOT stricter than the server's: a client rule rejecting something the server accepts would make a
 * legitimate name unclaimable with no way to find out why.
 */
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export type SubdomainValidationError = "empty" | "tooLong" | "invalid";

export type SubdomainValidation =
	| {valid: true; label: string}
	| {valid: false; error: SubdomainValidationError};

/**
 * Normalizes what the user typed into what the api expects: trimmed and lowercased. The api takes the
 * label in its exact stored form, so folding here is what lets someone type "ACME" and still succeed
 * rather than collecting a 400 for a difference they cannot see.
 */
export function normalizeLabel(raw: string): string {
	return raw.trim().toLowerCase();
}

export function validateLabel(raw: string): SubdomainValidation {
	const label = normalizeLabel(raw);
	if (label.length === 0) {
		return {valid: false, error: "empty"};
	}
	if (label.length > MAX_LABEL_LENGTH) {
		return {valid: false, error: "tooLong"};
	}
	if (!LABEL.test(label)) {
		return {valid: false, error: "invalid"};
	}
	return {valid: true, label};
}

/**
 * Shown when nothing is claimed yet: the English production suffix, derived from the deployed-site owner.
 * This remains a display fallback rather than claim topology: once a claim exists the editor renders the
 * api's `hosts` verbatim, because the server owns every address a claim actually serves.
 */
const FALLBACK_SUFFIX = `.${new URL(deployedSite("prod", "en").origin).hostname}`;

/**
 * The suffix to show beside the label input, derived from the brand the sender is currently browsing so
 * someone on transferify.ro is shown `.transferify.ro` rather than the other brand's address.
 *
 * A display affordance only, and deliberately conservative: any host that is not one of our brands (local
 * development, a preview deployment) falls back to the production suffix, because those hosts have no
 * claimed form at all and showing `.localhost` would promise an address nothing serves.
 *
 * Delegates to {@link brandHostFor} rather than testing the domain itself. It used to carry its own
 * `transferify\.(cloud|ro)$` regex, which agreed with the shared rule on the hosts that matter but not on
 * a claimed one — `acme.transferify.ro` came back as the suffix `.acme.transferify.ro`, an address one
 * label too deep for anything to serve. Unreachable in practice (the editor only renders on app hosts),
 * and removed rather than documented: ui#725 needs the SAME question answered a second time for the
 * address list, and two regexes answering it is how they drift.
 */
export function brandSuffixForHost(hostname: string | null | undefined): string {
	const brand = brandHostFor(hostname);
	return brand === null ? FALLBACK_SUFFIX : `.${brand}`;
}

/**
 * The claimed addresses to SHOW, out of every address the claim actually serves on.
 *
 * The claim genuinely serves on both brand zones (devops#137/#142) and nothing here changes that — the
 * other host keeps working, it just is not advertised on the wrong site. The owner's review of the Domain
 * tab (ui#725) was blunt about why: "if I'm on .ro it should not show .cloud". Advertising a competitor
 * brand's address to a sender who deliberately came to the Romanian site reads as a mistake, and the
 * address they would copy is the one they least want to hand out.
 *
 * Two fallbacks, both deliberately returning MORE rather than less:
 *
 * 1. No brand for the current host — local development, a preview deployment — means there is nothing to
 *    scope to, so every address is shown. Guessing a brand here would hide a real address behind a wrong
 *    guess, and the sender cannot tell what they are missing.
 * 2. A brand that matches NONE of the claim's hosts falls back to all of them. That combination should be
 *    impossible (the server derives the list from the same brands it serves), but a sender who holds a
 *    claim must never be shown an empty address list — an editor that renders nothing where an address
 *    belongs looks broken and gives them nothing to copy or act on.
 *
 * @param hosts the api's `hosts`, verbatim — absolute origins like `https://acme.transferify.ro`
 * @param hostname `window.location.hostname`, or null when there is no window (SSG prerender)
 */
export function claimHostsForBrand(
	hosts: readonly string[],
	hostname: string | null | undefined,
): string[] {
	const brand = brandHostFor(hostname);
	if (brand === null) {
		return [...hosts];
	}
	const scoped = hosts.filter((host) => hostBelongsToBrand(host, brand));
	return scoped.length > 0 ? scoped : [...hosts];
}

/**
 * Whether a claim origin sits under `brand`, compared on the parsed HOSTNAME rather than by substring.
 *
 * `endsWith` on the raw string would be wrong in both directions: it would match a look-alike registrable
 * domain (`nottransferify.ro` ends with `transferify.ro` once the dot is dropped) and it would be confused
 * by the scheme and any port. An unparseable entry is treated as belonging to no brand — it cannot be
 * shown as an address for a brand we cannot confirm.
 */
function hostBelongsToBrand(origin: string, brand: string): boolean {
	let host: string;
	try {
		host = new URL(origin).hostname.toLowerCase();
	} catch {
		return false;
	}
	return host === brand || host.endsWith(`.${brand}`);
}

/**
 * The editor's mutually exclusive states, so the component never re-derives them from three booleans.
 *
 * `lapsed` is the downgraded OWNER (ui#534): non-entitled, but still holding a claim. It is a state of its
 * own rather than a flavour of `locked` because the two owe the sender opposite things — `locked` offers an
 * upsell for something they do not have, while `lapsed` must show what they DO have and let them give it
 * back. Collapsing them is the bug this state was introduced to fix: the name stayed reserved against every
 * other account with no way for its owner to release it.
 *
 * It is not a flavour of `claimed` either. A downgraded owner's claim STOPS SERVING the moment the plan
 * lapses — `ClaimedSubdomainResolver` re-checks the live tier on every resolve, so links already sent on
 * that address 404 (`ClaimedSubdomainDownloadHttpTest.aDowngradedOwnersActiveClaimStopsServing…`). Rendering
 * it as `claimed` would show those addresses under "Your links live on", which is false.
 */
export type SubdomainSectionState = "locked" | "lapsed" | "loading" | "claimed" | "unclaimed";

export function subdomainSectionState(
	{entitled, loading, hasClaim}: {entitled: boolean; loading: boolean; hasClaim: boolean},
): SubdomainSectionState {
	// Loading wins for EVERY tier, because every tier now fetches: a non-entitled sender is indistinguishable
	// from a downgraded owner until the claim comes back, and answering "locked" early would flash an upsell
	// at someone who is about to be shown the name they hold.
	if (loading) {
		return "loading";
	}
	if (hasClaim) {
		return entitled ? "claimed" : "lapsed";
	}
	return entitled ? "unclaimed" : "locked";
}
